/* EVANO editorial purchase runtime v3: 2026-08-31 */
(() => {
  const body = document.body;
  if (!body.classList.contains('evano-pdp')) return;

  const normalize = (value) => value.trim().toLocaleLowerCase('es');
  const isEditorialFocus = body.classList.contains('evano-pdp--editorial-focus');
  const mainFormSelector = 'form[data-type="add-to-cart-form"]:not([id*="main-qs"]).is--main-sticky';
  const sizeGroupSelector = '[data-swatch-option].is-t4s-name__talla';
  let purchaseSyncQueued = false;
  let footerNearViewport = false;
  let stickyVisibilityFrame = 0;
  let observedPrimaryAction = null;
  let stickyPrimaryObserver = null;
  let observedFooter = null;
  let stickyFooterObserver = null;
  let stickyAddInFlight = false;
  let mainAddInFlight = false;
  let lastTrackedVariantId = '';
  let stickyPickerReturnFocus = null;
  let sizeGuideReturnFocus = null;
  let sizeGuideHistoryActive = false;
  let sizeGuideClosingFromHistory = false;

  const campaignAttribution = () => {
    const params = new URLSearchParams(window.location.search);
    const hasCampaign = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']
      .some((key) => params.has(key)) || params.has('fbclid');
    let attribution = {};

    try {
      attribution = JSON.parse(window.sessionStorage.getItem('evano-cro-attribution') || '{}');
      if (hasCampaign) {
        attribution = {
          utm_source: params.get('utm_source') || (params.has('fbclid') ? 'meta' : ''),
          utm_medium: params.get('utm_medium') || (params.has('fbclid') ? 'paid_social' : ''),
          utm_campaign: params.get('utm_campaign') || '',
          utm_content: params.get('utm_content') || '',
          landing_path: window.location.pathname
        };
        window.sessionStorage.setItem('evano-cro-attribution', JSON.stringify(attribution));
      }
    } catch (_) {
      attribution = {};
    }

    return attribution;
  };

  const pushCroEvent = (event, details = {}) => {
    window.dataLayer = window.dataLayer || [];
    const payload = {
      event,
      evano_event_version: '1.0',
      page_path: window.location.pathname,
      product_title: document.querySelector('.t4s-product__title')?.textContent.trim() || '',
      ...campaignAttribution(),
      ...details
    };
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('evano:cro-event', {
      detail: payload
    }));
  };

  const trackSizeSelection = (sizeOption) => {
    const form = sizeOption?.closest(mainFormSelector);
    const size = sizeOption?.dataset.value || sizeOption?.textContent.trim();
    if (!form || !size || sizeOption.disabled || sizeOption.getAttribute('aria-disabled') === 'true') return;

    window.setTimeout(() => {
      const selectedSize = confirmedSize(form.querySelector(sizeGroupSelector));
      if (selectedSize !== size) return;

      pushCroEvent('size_selected', {
        size,
        color: activeColor(form.querySelector('[data-swatch-option].is-t4s-name__color')),
        variant_id: form.querySelector('select[name="id"]')?.value || ''
      });
    }, 240);
  };

  const revealMetaOffer = () => {
    const offer = document.querySelector('[data-evano-meta-offer]');
    if (!offer) return;

    const params = new URLSearchParams(window.location.search);
    const source = `${params.get('utm_source') || ''} ${params.get('utm_medium') || ''}`;
    const fromMeta =
      params.has('fbclid') ||
      /facebook|instagram|meta/i.test(source) ||
      /facebook\.com|instagram\.com|l\.facebook\.com/i.test(document.referrer);
    let continueMetaSession = false;

    try {
      continueMetaSession = window.sessionStorage.getItem('evano-meta-offer') === '1';
      if (fromMeta) window.sessionStorage.setItem('evano-meta-offer', '1');
    } catch (_) {
      continueMetaSession = false;
    }

    offer.hidden = !(fromMeta || continueMetaSession);
    if (!offer.hidden && offer.dataset.evanoDiscountTracked !== 'true') {
      offer.dataset.evanoDiscountTracked = 'true';
      pushCroEvent('discount_revealed', {
        discount_code: 'NEW10',
        campaign_source: params.get('utm_source') || (params.has('fbclid') ? 'meta' : 'meta-session')
      });
    }
  };

  const mainProductForm = () => document.querySelector(mainFormSelector);
  const mainSizeGroup = () => mainProductForm()?.querySelector(sizeGroupSelector);
  const setPurchaseText = (element, text) => {
    if (element && element.textContent !== text) element.textContent = text;
  };

  const presentConfirmedSize = (group, value) => {
    if (!group || !value) return;

    group.classList.remove('is--evano-size-unconfirmed');
    group.dataset.evanoConfirmedValue = value;
    group.querySelectorAll('[data-swatch-item]').forEach((option) => {
      const selected = option.dataset.value === value;
      option.classList.toggle('is--selected', selected);
      if (option.getAttribute('role') === 'radio') {
        option.removeAttribute('aria-pressed');
        option.setAttribute('aria-checked', String(selected));
        option.tabIndex = selected ? 0 : -1;
      } else {
        option.setAttribute('aria-pressed', String(selected));
      }
    });
    group.querySelectorAll('[data-current-value]').forEach((label) => {
      setPurchaseText(label, value);
    });
  };

  const confirmedSize = (group = mainSizeGroup()) => {
    if (!group || group.classList.contains('is--evano-size-unconfirmed')) return null;

    const confirmed = group.dataset.evanoConfirmedValue
      || group.querySelector('[data-swatch-item].is--selected')?.dataset.value;
    if (!confirmed) return null;

    const form = group.closest(mainFormSelector);
    const select = form?.querySelector('select[name="id"]');
    const optionIndex = Number.parseInt(group.dataset.id || '', 10);
    const variant = productVariantsForForm(form)
      .find((candidate) => String(candidate.id) === String(select?.value || ''));
    const canonicalSize = Number.isInteger(optionIndex)
      ? variant?.options?.[optionIndex]
      : null;

    if (canonicalSize === confirmed) {
      presentConfirmedSize(group, confirmed);
      return confirmed;
    }

    return null;
  };

  const buttonIsUnavailable = (button) =>
    Boolean(
      button?.disabled ||
      button?.getAttribute('aria-disabled') === 'true' ||
      /agotado|sold out|no disponible/.test(normalize(button?.textContent || ''))
    );

  const setButtonText = (button, text) => {
    if (!button) return;

    const label = button.querySelector('.t4s-btn-atc_text') || button;
    if (label.textContent.trim() !== text) label.textContent = text;
  };

  const mainColorGroup = () =>
    mainProductForm()?.querySelector('[data-swatch-option].is-t4s-name__color');

  const activeColor = (group = mainColorGroup()) =>
    group?.querySelector('[data-current-value]')
      ?.textContent.trim() || '';

  const productVariantsCache = new WeakMap();
  const productVariantsForForm = (form) => {
    const source = form?.closest('.t4s-product-form__variants')
      ?.querySelector('.pr_variants_json');
    const text = source?.textContent;
    if (!text) return [];
    const cached = productVariantsCache.get(source);
    if (cached?.text === text) return cached.variants;

    try {
      const parsed = JSON.parse(text);
      const variants = Array.isArray(parsed) ? parsed : [];
      productVariantsCache.set(source, { text, variants });
      return variants;
    } catch (_) {
      productVariantsCache.delete(source);
      return [];
    }
  };

  const resolveVariantForSize = (form, sizeValue) => {
    const sizeGroup = form?.querySelector(sizeGroupSelector);
    if (!form || !sizeGroup || !sizeValue) return null;

    const selections = [...form.querySelectorAll('[data-swatch-option]')]
      .sort((a, b) => Number(a.dataset.id) - Number(b.dataset.id))
      .map((group) => group === sizeGroup
        ? sizeValue
        : group.querySelector('[data-swatch-item].is--selected')?.dataset.value || null);
    if (selections.some((value) => !value)) return null;

    return productVariantsForForm(form).find((variant) =>
      variant.options?.every((option, index) => option === selections[index])
    ) || null;
  };

  const commitCanonicalSize = (form, sizeValue) => {
    const sizeGroup = form?.querySelector(sizeGroupSelector);
    const variant = resolveVariantForSize(form, sizeValue);
    const select = form?.querySelector('select[name="id"]');
    if (!sizeGroup || !variant || !select) return null;

    presentConfirmedSize(sizeGroup, sizeValue);
    select.value = String(variant.id);
    syncVariantPrice(variant);
    showVariantMedia(variant);
    syncVariantUrl(variant.id);
    document.dispatchEvent(new CustomEvent('evano:explicit-variant-resolved', {
      detail: { form, variantId: String(variant.id) }
    }));
    return variant;
  };

  const showVariantMedia = (variant) => {
    const mediaId = String(variant?.featured_media?.id || '');
    if (!mediaId) return;

    const slides = [...document.querySelectorAll('[data-main-media] [data-main-slide]')];
    const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index < 0) return;

    const navItem = document.querySelectorAll('.t4s-carousel__nav .t4s-carousel__nav-item')[index];
    navItem?.click();
  };

  const formatCop = (cents) =>
    `$${Math.round(Number(cents || 0) / 100).toLocaleString('es-CO')} COP`;

  const syncVariantPrice = (variant) => {
    if (!variant || !Number.isFinite(Number(variant.price))) return;

    const currentPrice = formatCop(variant.price);
    const compareAtPrice = Number(variant.compare_at_price || 0);
    document.querySelectorAll(
      '.t4s-product__info-container [data-product-price], .t4s-product__info-container [data-product-unit-price]'
    ).forEach((price) => {
      if (price.querySelector('[data-unit-price]')) return;
      if (compareAtPrice > Number(variant.price)) {
        const previousText = formatCop(compareAtPrice);
        if (price.childElementCount === 2 && price.textContent === previousText + ' ' + currentPrice &&
            price.querySelector('del')?.textContent === previousText &&
            price.querySelector('ins')?.textContent === currentPrice) return;
        const previous = document.createElement('del');
        const current = document.createElement('ins');
        previous.textContent = previousText;
        current.textContent = currentPrice;
        price.replaceChildren(previous, document.createTextNode(' '), current);
      } else if (price.textContent !== currentPrice || price.querySelector('del, ins')) {
        price.textContent = currentPrice;
      }
    });
    document.querySelectorAll('[data-sticky-price]').forEach((price) => {
      setPurchaseText(price, currentPrice);
    });
  };

  const selectEditorialColor = (source) => {
    const group = source?.closest('[data-swatch-option].is-t4s-name__color');
    const form = group?.closest(mainFormSelector);
    const value = source?.dataset.value || source?.textContent.trim();
    if (!group || !form || !value) return false;

    group.querySelectorAll('[data-swatch-item]').forEach((option) => {
      const selected = option === source;
      option.classList.toggle('is--selected', selected);
      option.setAttribute('aria-pressed', String(selected));
    });
    group.querySelectorAll('[data-current-value]').forEach((label) => {
      label.textContent = value;
    });

    const variants = productVariantsForForm(form);
    const optionIndex = Number.parseInt(group.dataset.id || '', 10);
    const size = confirmedSize(form.querySelector(sizeGroupSelector));
    const selections = [...form.querySelectorAll('[data-swatch-option]')]
      .sort((a, b) => Number(a.dataset.id) - Number(b.dataset.id))
      .map((optionGroup) => {
        if (optionGroup === group) return value;
        if (optionGroup.matches(sizeGroupSelector)) return size;
        return optionGroup.querySelector('[data-swatch-item].is--selected')?.dataset.value || null;
      });
    const exactVariant = selections.every(Boolean)
      ? variants.find((variant) => variant.options?.every((option, index) => option === selections[index]))
      : null;
    const mediaVariant = exactVariant
      || variants.find((variant) => variant.available && variant.options?.[optionIndex] === value)
      || variants.find((variant) => variant.options?.[optionIndex] === value);

    showVariantMedia(mediaVariant);

    const select = form.querySelector('select[name="id"]');
    if (select) {
      select.value = exactVariant ? String(exactVariant.id) : '';
    }
    syncVariantPrice(exactVariant);
    syncVariantUrl(exactVariant?.id || '');
    schedulePurchaseSync();
    window.setTimeout(schedulePurchaseSync, 180);
    document.dispatchEvent(new CustomEvent('evano:explicit-variant-resolved', {
      detail: { form, variantId: exactVariant ? String(exactVariant.id) : '' }
    }));
    return true;
  };

  const selectedVariantOption = (form = mainProductForm()) => {
    const select = form?.querySelector('select[name="id"]');
    return select?.value
      ? [...select.options].find((option) => option.value === select.value) || null
      : null;
  };

  const selectedVariantForForm = (form = mainProductForm()) => {
    const variantId = form?.querySelector('select[name="id"]')?.value;
    if (!variantId) return null;

    return productVariantsForForm(form).find((variant) =>
      String(variant.id) === String(variantId)
    ) || null;
  };

  const syncDeepLinkedVariantPrice = () => {
    const requestedVariantId = new URL(window.location.href).searchParams.get('variant');
    const variant = selectedVariantForForm();
    if (requestedVariantId && String(variant?.id || '') === requestedVariantId) {
      syncVariantPrice(variant);
    }
  };

  const syncSelectedVariantAvailability = (available) => {
    if (typeof available !== 'boolean') return;

    document.querySelectorAll('[data-product-available]').forEach((wrapper) => {
      const inStock = wrapper.querySelector('[data-available-status]');
      const soldOut = wrapper.querySelector('[data-soldout-status]');

      inStock?.classList.toggle('t4s-dn', !available);
      soldOut?.classList.toggle('t4s-dn', available);
      wrapper.setAttribute(
        'aria-label',
        available ? 'Variante disponible' : 'Variante agotada'
      );
    });
  };

  const syncVariantUrl = (variantId) => {
    const url = new URL(window.location.href);
    if (variantId) url.searchParams.set('variant', variantId);
    else url.searchParams.delete('variant');
    const next = `${url.pathname}${url.search}${url.hash}`;
    if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== next) {
      window.history.replaceState(window.history.state, '', next);
    }
  };

  const cartCount = () => {
    const counter = document.querySelector('[data-cart-count]');
    const value = counter?.dataset.cartCountValue || counter?.textContent;
    const count = Number.parseInt(value || '0', 10);
    return Number.isFinite(count) ? count : 0;
  };

  const setPickerStatus = (picker, state, message) => {
    const status = picker?.querySelector('.evano-sticky-size-picker__status');
    if (!status) return;
    status.dataset.state = state;
    status.textContent = message;
  };

  const miniCartIsCurrent = (expectedCount, expectedVariantId = '') => {
    const drawer = document.querySelector('#t4s-mini_cart');
    if (!drawer || Number(drawer.dataset.ccount) !== Number(expectedCount)) return false;

    const escapedVariantId = window.CSS?.escape
      ? window.CSS.escape(String(expectedVariantId))
      : String(expectedVariantId).replace(/["\\]/g, '\\$&');
    const hasExpectedItem = !expectedVariantId || Boolean(
      drawer.querySelector(`[data-cart-item][data-vid="${escapedVariantId}"]`)
    );

    return expectedCount === 0
      ? Boolean(drawer.querySelector('.t4s-mini_cart__emty'))
      : Boolean(drawer.querySelector('[data-cart-item]')) && hasExpectedItem;
  };

  const parseMiniCartSection = (html) => {
    if (!html || !('DOMParser' in window)) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const content = parsed.querySelector('#t4s-mini_cart')
      || parsed.querySelector('#shopify-section-mini_cart');
    const items = content?.querySelector('[data-cart-items]');
    if (!items) return null;
    const countValue = content.getAttribute('data-ccount') ?? items.getAttribute('data-ccount');
    if (countValue == null || countValue.trim() === '') return null;
    const count = Number(countValue);
    return Number.isInteger(count) && count >= 0 ? { content, count } : null;
  };

  const refreshCartSurface = (response, nextCount, expectedVariantId = '') => {
    const sectionHtml = response.sections?.mini_cart;
    const currentDrawer = document.querySelector('#t4s-mini_cart');
    let renderedCount = Number(nextCount);
    if (sectionHtml && currentDrawer && 'DOMParser' in window) {
      const fresh = parseMiniCartSection(sectionHtml);
      if (fresh) {
        renderedCount = fresh.count;
        currentDrawer.innerHTML = fresh.content.innerHTML;
        currentDrawer.dataset.ccount = String(renderedCount);
      }
    }

    document.documentElement.classList.toggle('t4s-cart-count-0', renderedCount === 0);

    document.querySelectorAll('[data-cart-count]').forEach((counter) => {
      counter.textContent = String(renderedCount);
      counter.dataset.cartCountValue = String(renderedCount);
      counter.setAttribute('aria-hidden', String(renderedCount === 0));
      counter.classList.toggle('t4s-op-0', renderedCount === 0);
    });

    document.dispatchEvent(new CustomEvent('evano:cart-added', {
      detail: { item: response, itemCount: renderedCount }
    }));

    return miniCartIsCurrent(renderedCount, expectedVariantId);
  };

  const closeStickySizePicker = ({ restoreFocus = true } = {}) => {
    const picker = document.querySelector('.evano-sticky-size-picker');
    if (!picker) return;
    picker.hidden = true;
    picker.classList.remove('is--open');
    picker.closest('[data-sticky-addtocart]')?.classList.remove('evano-variant-picker-open');
    if (restoreFocus && stickyPickerReturnFocus?.isConnected) {
      const sticky = stickyPickerReturnFocus.closest('[data-sticky-addtocart]');
      if (!sticky?.hasAttribute('inert') && stickyPickerReturnFocus.getClientRects().length) {
        stickyPickerReturnFocus.focus({ preventScroll: true });
      }
    }
    stickyPickerReturnFocus = null;
  };

  const variantSizeAvailability = (form = mainProductForm()) => {
    const sizeGroup = form?.querySelector(sizeGroupSelector);
    const groups = [...form?.querySelectorAll('[data-swatch-option]') || []]
      .sort((a, b) => Number(a.dataset.id) - Number(b.dataset.id));
    const sizeIndex = groups.indexOf(sizeGroup);
    const variants = productVariantsForForm(form);
    const availability = new Map();
    if (!sizeGroup || sizeIndex < 0 || !variants.length) return availability;

    const selections = groups.map((group) =>
      group.querySelector('[data-swatch-item].is--selected')?.dataset.value || null
    );
    sizeGroup.querySelectorAll('[data-swatch-item]').forEach((option) => {
      const size = option.dataset.value || option.textContent.trim();
      const available = variants.some((variant) =>
        variant.available &&
        variant.options?.[sizeIndex] === size &&
        groups.every((_, index) =>
          index === sizeIndex || !selections[index] || variant.options?.[index] === selections[index]
        )
      );
      availability.set(size, available);
    });
    return availability;
  };

  const sizeAvailability = (sticky = document.querySelector('[data-sticky-addtocart]')) => {
    const canonical = variantSizeAvailability();
    if (canonical.size) return canonical;

    const color = activeColor();
    const availability = new Map();
    [...sticky?.querySelectorAll('[data-sticky-select] [data-dropdown-item]') || []]
      .filter((item) => {
        const [, ...colorParts] = item.textContent.trim().split(' / ');
        const itemColor = colorParts.join(' / ').replace(/\s+-\s+(agotado|sold out).*$/i, '');
        return !color || normalize(itemColor) === normalize(color);
      })
      .forEach((item) => {
        const size = item.textContent.trim().split(' / ')[0]
          .replace(/\s+-\s+(agotado|sold out).*$/i, '');
        const available = !item.disabled && !/agotado|sold out/i.test(item.textContent);
        availability.set(size, Boolean(availability.get(size)) || available);
      });
    return availability;
  };

  const syncMainSizeAvailability = () => {
    const group = mainSizeGroup();
    const availability = sizeAvailability();
    if (!group || !availability.size) return;
    const soldOutSelectionEnabled = Boolean(
      mainProductForm()?.querySelector('[data-notify-stock-btn], [data-evano-notify-stock]')
    );

    group.querySelectorAll('[data-swatch-item]').forEach((option) => {
      const value = option.dataset.value || option.textContent.trim();
      const available = availability.get(value) !== false;
      option.classList.toggle('is--soldout', !available);
      const blocked = !available && !soldOutSelectionEnabled;
      option.disabled = blocked;
      option.setAttribute('aria-disabled', String(blocked));
      option.setAttribute(
        'aria-label',
        available
          ? `Talla ${value}`
          : soldOutSelectionEnabled
            ? `Talla ${value} — Agotada; seleccionar para recibir aviso`
            : `Talla ${value} — Agotada`
      );
    });
  };

  const wait = (delay) => new Promise((resolve) => window.setTimeout(resolve, delay));

  const openMiniCart = async (source = 'sticky_add_to_cart') => {
    const triggers = [...document.querySelectorAll(
      'a[data-drawer-options*="#t4s-mini_cart"], a[aria-label="Carrito de compras"]'
    )];
    const trigger = triggers.find((item) => item.getClientRects().length > 0) || triggers[0];
    if (!trigger) return false;
    closeStickySizePicker({ restoreFocus: false });
    const drawerIsOpen = () =>
      document.querySelector('#t4s-mini_cart')?.getAttribute('aria-hidden') === 'false';
    const waitForDrawer = async (timeoutMs) => {
      const startedAt = Date.now();
      while (Date.now() - startedAt < timeoutMs) {
        if (drawerIsOpen()) return true;
        await wait(50);
      }
      return drawerIsOpen();
    };

    if (drawerIsOpen()) return true;

    trigger.click();
    if (await waitForDrawer(1600)) {
      pushCroEvent('cart_opened', { source });
      return true;
    }
    return false;
  };

  const requestJson = async (url, timeoutMs = 6500) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      return response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const hydrateMiniCart = async (expectedVariantId = '') => {
    const root = window.Shopify?.routes?.root || '/';
    let cart = null;
    let sectionHtml = '';

    for (let attempt = 0; attempt < 3; attempt += 1) {
      await wait(attempt === 0 ? 120 : 240);
      const cartUrl = `${root}cart.js?_evano=${Date.now()}`;
      // Section Rendering normally returns the drawer contents without its wrapper.
      const sectionUrl = new URL(root, window.location.origin);
      sectionUrl.searchParams.set('sections', 'mini_cart');
      sectionUrl.searchParams.set('_evano', String(Date.now()));
      const [nextCart, sections] = await Promise.all([
        requestJson(cartUrl),
        requestJson(sectionUrl.toString())
      ]);
      cart = nextCart;
      const candidate = sections?.mini_cart || '';
      const fresh = parseMiniCartSection(candidate);

      const escapedVariantId = window.CSS?.escape
        ? window.CSS.escape(String(expectedVariantId))
        : String(expectedVariantId).replace(/["\\]/g, '\\$&');
      const hasExpectedItem = !expectedVariantId || Boolean(
        fresh?.content.querySelector(`[data-cart-item][data-vid="${escapedVariantId}"]`)
      );

      if (fresh && fresh.count === Number(cart.item_count) && hasExpectedItem) {
        sectionHtml = candidate;
        break;
      }
    }

    if (!sectionHtml) throw new Error('Mini cart section did not match the cart.');

    refreshCartSurface(
      { sections: { mini_cart: sectionHtml } },
      Number(cart.item_count) || 0,
      expectedVariantId
    );
    return cart;
  };

  const syncMiniCartAfterAdd = async (expectedVariantId, source) => {
    try {
      const cart = await hydrateMiniCart(expectedVariantId);
      return miniCartIsCurrent(Number(cart.item_count) || 0, expectedVariantId);
    } catch (_) {
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { source }
      }));
      return false;
    }
  };

  const addCartItem = async (payload) => {
    let response;
    const retryDelays = [700, 1400, 2600, 4200];
    const requestPayload = {
      ...payload,
      sections: 'mini_cart',
      sections_url: window.location.pathname || '/'
    };

    for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 10000);

      try {
        response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestPayload),
          signal: controller.signal
        });
      } finally {
        window.clearTimeout(timeout);
      }

      if (response.status !== 429 || attempt === retryDelays.length) return response;

      const retryAfter = Number.parseFloat(response.headers.get('Retry-After'));
      await wait(
        Number.isFinite(retryAfter)
          ? Math.max(retryAfter * 1000, retryDelays[attempt])
          : retryDelays[attempt]
      );
    }

    return response;
  };

  const editorialCartErrorMessage = (error) => {
    const description = String(error.message || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (error.status === 429) return 'El carrito está ocupado. Espera unos segundos y vuelve a intentar.';
    if (error.status === 409) return 'El carrito recibió dos acciones al mismo tiempo. Inténtalo de nuevo.';
    if (error.status === 422) {
      if (/maximum quantity|can.?t add more|only available quantity|cantidad maxima/.test(description)) {
        return 'La cantidad supera el máximo disponible de esta talla. Revisa tu carrito.';
      }
      if (/sold out|agotad/.test(description)) return 'Esta talla se agotó hace un momento. Elige otra.';
      return 'No pudimos añadir esta talla. Revisa la cantidad en tu carrito o elige otra talla.';
    }
    return 'No pudimos añadir el producto. Revisa tu conexión e inténtalo de nuevo.';
  };

  const submitStickySelection = async (event) => {
    if (stickyAddInFlight) return;

    const picker = event.currentTarget.closest('.evano-sticky-size-picker');
    const form = mainProductForm();
    const size = confirmedSize();
    const colorGroup = mainColorGroup();
    const color = activeColor(colorGroup);
    const mainAdd = form?.querySelector('[data-atc-form]');
    const selectedOption = selectedVariantOption(form);
    const variants = productVariantsForForm(form);
    const variant = variants.find((candidate) =>
      String(candidate.id) === String(selectedOption?.value || '')
    );
    const resolvedVariant = resolveVariantForSize(form, size);
    const variantMatchesSelection = variant
      && resolvedVariant
      && String(resolvedVariant.id) === String(variant.id);
    const variantId = variantMatchesSelection ? String(variant.id) : '';
    const selectionLabel = color ? `${color} · Talla ${size}` : `Talla ${size}`;
    const quantity = Number.parseInt(form?.querySelector('[name="quantity"]')?.value || '1', 10);
    const submit = event.currentTarget;

    if (!size || (colorGroup && !color)) {
      setPickerStatus(
        picker,
        'error',
        colorGroup ? 'Elige color y talla para continuar.' : 'Elige una talla para continuar.'
      );
      buildStickySizePicker();
      return;
    }
    if (!variantId || !variant?.available || !mainAdd || buttonIsUnavailable(mainAdd)) {
      setPickerStatus(picker, 'error', 'Esta combinación no está disponible. Elige otra.');
      return;
    }

    const before = cartCount();
    stickyAddInFlight = true;
    submit.disabled = true;
    submit.textContent = 'Añadiendo…';
    setPickerStatus(picker, 'loading', selectionLabel);

    try {
      const response = await addCartItem({
        items: [{ id: Number(variantId), quantity: Number.isFinite(quantity) ? quantity : 1 }]
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(result.description || 'Cart request failed');
        error.status = response.status;
        throw error;
      }
      if (!document.body.contains(picker)) return;
      const nextCount = before + (Number.isFinite(quantity) ? quantity : 1);
      const cartReady = refreshCartSurface(result, nextCount, variantId);
      const miniCartSync = cartReady
        ? Promise.resolve(true)
        : syncMiniCartAfterAdd(variantId, 'evano-product-editorial');
      submit.textContent = 'Añadido al carrito';
      setPickerStatus(picker, 'success', `${selectionLabel} añadida`);
      pushCroEvent('add_to_cart_success', {
        source: 'sticky_picker',
        variant_id: String(variantId),
        size,
        color,
        quantity: Number.isFinite(quantity) ? quantity : 1
      });
      // Open only after the confirmed addition is visible in the cart surface.
      const miniCartReady = await miniCartSync;
      if (miniCartReady && await openMiniCart('sticky_picker')) return;

      closeStickySizePicker();
      window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
    } catch (error) {
      if (!document.body.contains(picker)) return;
      submit.disabled = false;
      submit.textContent = 'Añadir al carrito';
      const message = editorialCartErrorMessage(error);
      setPickerStatus(picker, 'error', message);
      pushCroEvent('add_to_cart_error', {
        source: 'sticky_picker',
        variant_id: String(variantId || ''),
        size: size || '',
        color,
        status: Number(error.status) || 0
      });
    } finally {
      stickyAddInFlight = false;
    }
  };

  const setMainAddStatus = (form, type = '', message = '') => {
    form?.querySelectorAll('.evano-variant-choice-error').forEach((node) => node.remove());
    if (!form || !message) return;

    const status = document.createElement('p');
    status.className = 'evano-variant-choice-error';
    status.dataset.evanoStatus = type;
    status.setAttribute('role', type === 'error' ? 'alert' : 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = message;
    const buttons = form.querySelector('.t4s-product-form__buttons');
    (buttons || form.querySelector('[data-atc-form]'))?.after(status);
  };

  const lineItemFromForm = (form, variantId, quantity) => {
    const item = { id: Number(variantId), quantity };
    const properties = {};

    new FormData(form).forEach((value, key) => {
      if (typeof value !== 'string' || !value) return;
      const property = key.match(/^properties\[(.+)]$/);
      if (property) properties[property[1]] = value;
      if (key === 'selling_plan') item.selling_plan = value;
    });
    if (Object.keys(properties).length) item.properties = properties;
    return item;
  };

  const submitMainSelection = async (event, submit) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (mainAddInFlight) return;

    const form = submit?.closest(mainFormSelector);
    const size = confirmedSize(form?.querySelector(sizeGroupSelector));
    const colorGroup = form?.querySelector('[data-swatch-option].is-t4s-name__color');
    const color = activeColor(colorGroup);
    const variant = resolveVariantForSize(form, size);
    const quantityValue = Number.parseInt(form?.querySelector('[name="quantity"]')?.value || '1', 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const label = submit?.querySelector('.t4s-btn-atc_text') || submit;

    if (!form || !size || (colorGroup && !color)) {
      setMainAddStatus(
        form,
        'error',
        colorGroup ? 'Elige color y talla para continuar.' : 'Elige una talla para continuar.'
      );
      form?.querySelector(sizeGroupSelector)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!variant?.available) {
      setMainAddStatus(form, 'error', 'Esta combinación no está disponible. Elige otra.');
      return;
    }

    commitCanonicalSize(form, size);
    const variantId = String(variant.id);
    const before = cartCount();
    mainAddInFlight = true;
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    label.textContent = 'Añadiendo…';
    setMainAddStatus(form);

    try {
      const response = await addCartItem({
        items: [lineItemFromForm(form, variantId, quantity)]
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(result.description || 'Cart request failed');
        error.status = response.status;
        throw error;
      }

      const nextCount = before + quantity;
      const cartReady = refreshCartSurface(result, nextCount, variantId);
      const miniCartSync = cartReady
        ? Promise.resolve(true)
        : syncMiniCartAfterAdd(variantId, 'evano-product-editorial-main');

      label.textContent = 'Añadido al carrito';
      pushCroEvent('add_to_cart_success', {
        source: 'main_cta',
        variant_id: variantId,
        size,
        color,
        quantity
      });
      document.dispatchEvent(new CustomEvent('evano:cart-added', {
        detail: { source: 'main_cta', variantId }
      }));
      const miniCartReady = await miniCartSync;
      if (miniCartReady && await openMiniCart('main_cta')) return;
      window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
    } catch (error) {
      const message = editorialCartErrorMessage(error);
      setMainAddStatus(form, 'error', message);
      pushCroEvent('add_to_cart_error', {
        source: 'main_cta',
        variant_id: variantId,
        size,
        color,
        status: Number(error.status) || 0
      });
    } finally {
      mainAddInFlight = false;
      schedulePurchaseSync();
      if (document.body.contains(submit)) {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        if (label.textContent === 'Añadiendo…' || label.textContent === 'Añadido al carrito') {
          label.textContent = 'Añadir al carrito';
        }
      }
    }
  };

  const buildStickySizePicker = () => {
    const sticky = document.querySelector('[data-sticky-addtocart]');
    const form = mainProductForm();
    const sizeGroup = mainSizeGroup();
    const colorGroup = form?.querySelector('[data-swatch-option].is-t4s-name__color');
    if (!sticky || !sizeGroup) return null;

    let picker = sticky.querySelector('.evano-sticky-size-picker');
    if (!picker) {
      picker = document.createElement('div');
      picker.className = 'evano-sticky-size-picker';
      picker.hidden = true;
      picker.setAttribute('role', 'dialog');
      picker.setAttribute('aria-label', 'Elige los detalles del producto');
      picker.innerHTML = `
        <div class="evano-sticky-size-picker__header">
          <p class="evano-sticky-size-picker__title">Elige los detalles</p>
          <button type="button" class="evano-sticky-size-picker__close" aria-label="Cerrar selector de variantes">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </div>
        <div class="evano-sticky-size-picker__field">
          <p class="evano-sticky-size-picker__label">Color: <span data-picker-color></span></p>
          <div class="evano-sticky-size-picker__colors" role="group" aria-label="Colores disponibles"></div>
        </div>
        <div class="evano-sticky-size-picker__field">
          <p class="evano-sticky-size-picker__label">Talla: <span data-picker-size>Selecciona una talla</span></p>
          <div class="evano-sticky-size-picker__options" role="radiogroup" aria-label="Tallas disponibles"></div>
        </div>
        <button type="button" class="evano-sticky-size-picker__submit">Añadir al carrito</button>
        <p class="evano-sticky-size-picker__status" role="status" aria-live="polite"></p>
      `;
      sticky.querySelector('.t4s-sticky-atc__btns')?.before(picker);
      picker.querySelector('.evano-sticky-size-picker__close')
        ?.addEventListener('click', closeStickySizePicker);
      picker.querySelector('.evano-sticky-size-picker__submit')
        ?.addEventListener('click', submitStickySelection);
    }

    const color = activeColor(colorGroup);
    const selected = confirmedSize(sizeGroup);
    if (selected) presentConfirmedSize(sizeGroup, selected);
    const availability = sizeAvailability(sticky);

    const colorValue = picker.querySelector('[data-picker-color]');
    const sizeValue = picker.querySelector('[data-picker-size]');
    const colorField = picker.querySelector('.evano-sticky-size-picker__colors')
      ?.closest('.evano-sticky-size-picker__field');
    if (colorField) colorField.hidden = !colorGroup;
    if (colorValue) colorValue.textContent = color || 'Selecciona un color';
    if (sizeValue) sizeValue.textContent = selected || 'Selecciona una talla';

    const colors = picker.querySelector('.evano-sticky-size-picker__colors');
    colors.replaceChildren();
    colorGroup?.querySelectorAll('[data-swatch-item]').forEach((source) => {
      const value = source.dataset.value || source.textContent.trim();
      const option = document.createElement('button');
      const preview = document.createElement('span');
      option.type = 'button';
      option.className = 'evano-sticky-size-picker__color';
      option.dataset.value = value;
      option.setAttribute('aria-label', `Color ${value}`);
      option.setAttribute('aria-pressed', String(normalize(value) === normalize(color)));
      if (normalize(value) === normalize(color)) option.classList.add('is--selected');
      preview.className = 'evano-sticky-size-picker__color-preview';
      const sourceImage = source.dataset.bg ||
        getComputedStyle(source).backgroundImage.match(/url\(["']?(.*?)["']?\)/)?.[1];
      if (sourceImage) {
        const imageUrl = sourceImage.startsWith('//')
          ? `${window.location.protocol}${sourceImage}`
          : sourceImage;
        preview.style.backgroundImage = `url("${imageUrl.replace(/"/g, '%22')}")`;
      }
      option.append(preview);
      option.addEventListener('click', () => {
        source.click();
        setPickerStatus(picker, '', '');
        window.setTimeout(() => {
          const rebuilt = buildStickySizePicker();
          if (rebuilt) rebuilt.hidden = false;
        }, 220);
      });
      colors.append(option);
    });

    const options = picker.querySelector('.evano-sticky-size-picker__options');
    options.replaceChildren();
    sizeGroup.querySelectorAll('[data-swatch-item]').forEach((source) => {
      const value = source.dataset.value || source.textContent.trim();
      const available = availability.has(value) ? availability.get(value) : !source.disabled;
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'evano-sticky-size-picker__option';
      option.dataset.value = value;
      option.textContent = value;
      option.disabled = !available;
      option.setAttribute('role', 'radio');
      option.setAttribute('aria-disabled', String(!available));
      option.setAttribute('aria-label', available ? `Talla ${value}` : `Talla ${value}, agotada`);
      option.removeAttribute('aria-pressed');
      option.setAttribute('aria-checked', String(selected === value));
      option.tabIndex = selected === value ? 0 : -1;
      if (selected === value) option.classList.add('is--selected');
      option.addEventListener('click', () => {
        const source = [...sizeGroup.querySelectorAll('[data-swatch-item]')]
          .find((item) => item.dataset.value === value);
        source?.click();
        commitCanonicalSize(form, value);
        setPickerStatus(picker, '', '');
        window.setTimeout(() => {
          commitCanonicalSize(form, value);
          const rebuilt = buildStickySizePicker();
          if (rebuilt) {
            rebuilt.hidden = false;
            rebuilt.querySelector(`.evano-sticky-size-picker__option[data-value="${value}"]`)
              ?.focus({ preventScroll: true });
          }
        }, 120);
        schedulePurchaseSync();
        window.setTimeout(schedulePurchaseSync, 180);
        window.setTimeout(schedulePurchaseSync, 700);
      });
      options.append(option);
    });
    if (!options.querySelector('[tabindex="0"]')) {
      const firstAvailable = options.querySelector('.evano-sticky-size-picker__option:not([disabled])');
      if (firstAvailable) firstAvailable.tabIndex = 0;
    }

    const selectedAvailable = selected && availability.get(selected) !== false;
    const submit = picker.querySelector('.evano-sticky-size-picker__submit');
    const hasCompleteSelection = selectedAvailable && (!colorGroup || color);
    submit.disabled = !hasCompleteSelection;
    submit.textContent = 'Añadir al carrito';
    submit.setAttribute(
      'aria-label',
      hasCompleteSelection
        ? color
          ? `Añadir color ${color}, talla ${selected}, al carrito`
          : `Añadir talla ${selected} al carrito`
        : colorGroup
          ? 'Añadir al carrito; elige color y talla'
          : 'Añadir al carrito; elige una talla'
    );

    return picker;
  };

  const openStickySizePicker = (picker = buildStickySizePicker(), trigger = null) => {
    if (!picker) return;
    if (!stickyAddInFlight) setPickerStatus(picker, '', '');
    stickyPickerReturnFocus = trigger || document.activeElement;
    picker.hidden = false;
    picker.classList.add('is--open');
    picker.closest('[data-sticky-addtocart]')?.classList.add('evano-variant-picker-open');
    pushCroEvent('sticky_opened', {
      size: confirmedSize() || '',
      color: activeColor(),
      variant_id: mainProductForm()?.querySelector('select[name="id"]')?.value || ''
    });
    window.requestAnimationFrame(() => {
      picker.querySelector('.evano-sticky-size-picker__color.is--selected, .evano-sticky-size-picker__option:not([disabled])')
        ?.focus({ preventScroll: true });
    });
  };

  const handleStickyAdd = (event) => {
    const picker = buildStickySizePicker();
    if (!picker) {
      schedulePurchaseSync();
      return false;
    }

    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();
    openStickySizePicker(picker, event?.target?.closest?.('[data-action-atc]'));
    schedulePurchaseSync();
    return true;
  };

  const syncPurchaseLabels = () => {
    purchaseSyncQueued = false;
    if (!isEditorialFocus || mainAddInFlight) return;

    const form = mainProductForm();
    const sizeGroup = mainSizeGroup();
    const sticky = document.querySelector('[data-sticky-addtocart]');
    const mainAdd = form?.querySelector('[data-atc-form]');
    const stickyAdd = sticky?.querySelector('[data-action-atc]');
    if (!form || !sizeGroup || !mainAdd || !stickyAdd) return;

    syncMainSizeAvailability();

    const size = confirmedSize(sizeGroup);
    if (size) presentConfirmedSize(sizeGroup, size);
    const variant = selectedVariantForForm(form);
    const available = Boolean(size && variant?.available);
    const variantId = available ? String(variant.id) : '';
    const notifyButton = form.querySelector('[data-evano-notify-stock]');
    let context = sticky.querySelector('.evano-sticky-purchase-context');

    if (!context) {
      context = document.createElement('p');
      context.className = 'evano-sticky-purchase-context';
      context.setAttribute('aria-live', 'polite');
      sticky.querySelector('.t4s-sticky-atc__btns')?.prepend(context);
    }

    if (!size) {
      notifyButton?.classList.add('t4s-d-none');
      setButtonText(mainAdd, 'Selecciona una talla');
      mainAdd.setAttribute('aria-label', 'Selecciona una talla');
      mainAdd.dataset.evanoPurchaseState = 'needs-size';
      setButtonText(stickyAdd, 'Elegir talla');
      stickyAdd.setAttribute('aria-label', 'Elegir talla');
      stickyAdd.dataset.variantId = '';
      stickyAdd.dataset.evanoPurchaseState = 'needs-size';
      setPurchaseText(context, 'Selecciona tu talla');
      syncVariantUrl('');
      return;
    }

    syncSelectedVariantAvailability(available);
    syncVariantPrice(variant);

    const price = sticky.querySelector('[data-sticky-price]')?.textContent.trim() || '';
    setPurchaseText(context, `Talla ${size}${price ? ` · ${price}` : ''}`);
    stickyAdd.dataset.variantId = variantId;

    if (!available) {
      setButtonText(mainAdd, 'Agotado');
      mainAdd.setAttribute('aria-label', `Talla ${size}, agotada`);
      mainAdd.setAttribute('aria-disabled', 'true');
      mainAdd.dataset.evanoPurchaseState = 'sold-out';
      setButtonText(stickyAdd, 'Agotado');
      stickyAdd.setAttribute('aria-label', `Talla ${size}, agotada`);
      stickyAdd.setAttribute('aria-disabled', 'true');
      stickyAdd.dataset.evanoPurchaseState = 'sold-out';
      if (notifyButton && variant) {
        notifyButton.dataset.variantId = String(variant.id);
        notifyButton.dataset.storageid = `notify-stock${variant.id}`;
        notifyButton.classList.remove('t4s-d-none');
        setPurchaseText(
          notifyButton.querySelector('[data-evano-notify-label]'),
          `Avísame cuando vuelva la talla ${size}`
        );
      }
      return;
    }

    notifyButton?.classList.add('t4s-d-none');
    setButtonText(mainAdd, 'Añadir al carrito');
    mainAdd.setAttribute('aria-label', `Añadir talla ${size} al carrito`);
    mainAdd.removeAttribute('aria-disabled');
    mainAdd.disabled = false;
    mainAdd.dataset.evanoPurchaseState = 'ready';
    setButtonText(stickyAdd, 'Añadir al carrito');
    stickyAdd.setAttribute('aria-label', `Añadir talla ${size} al carrito`);
    stickyAdd.removeAttribute('aria-disabled');
    stickyAdd.dataset.evanoPurchaseState = 'ready';
    syncVariantUrl(variantId);
  };

  const schedulePurchaseSync = () => {
    if (!isEditorialFocus || purchaseSyncQueued) return;
    purchaseSyncQueued = true;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(syncPurchaseLabels);
    });
  };

  const visible = (element) =>
    Boolean(element && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');

  const blockingPurchaseSurfaceOpen = () => {
    const selectors = [
      '#t4s-mini_cart[aria-hidden="false"]',
      '.mfp-wrap:not(.mfp-hide)',
      '.pswp--open',
      '[role="dialog"][aria-modal="true"]',
      '.t4s-modal:not([hidden])'
    ];
    return selectors.some((selector) =>
      [...document.querySelectorAll(selector)].some((element) =>
        !element.closest('[data-sticky-addtocart]') && visible(element)
      )
    );
  };

  let primaryActionVisible = null;
  const primaryActionIntersectsViewport = () => {
    const mainAdd = mainProductForm()?.querySelector('[data-atc-form]');
    if (!mainAdd) return false;
    if (stickyPrimaryObserver && observedPrimaryAction === mainAdd && primaryActionVisible !== null) {
      return primaryActionVisible;
    }

    const rect = mainAdd.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0
      && rect.top < viewportHeight
      && rect.right > 0
      && rect.left < viewportWidth;
  };

  const applyStickySuppression = () => {
    const sticky = document.querySelector('[data-sticky-addtocart]');
    if (!sticky) return;
    const primaryVisible = primaryActionIntersectsViewport();
    const suppressed = primaryVisible || footerNearViewport || blockingPurchaseSurfaceOpen();
    sticky.classList.toggle('evano-sticky-suppressed', suppressed);
    if (suppressed) {
      sticky.setAttribute('aria-hidden', 'true');
      sticky.setAttribute('inert', '');
    } else {
      sticky.removeAttribute('aria-hidden');
      sticky.removeAttribute('inert');
    }
    if (suppressed) {
      const picker = sticky.querySelector('.evano-sticky-size-picker');
      const focusWasInsidePicker = Boolean(picker?.contains(document.activeElement));
      closeStickySizePicker({ restoreFocus: false });
      if (focusWasInsidePicker) {
        const mainAdd = mainProductForm()?.querySelector('[data-atc-form]');
        const selectedSize = mainSizeGroup()
          ?.querySelector('[data-swatch-item].is--selected:not([disabled])');
        const fallback = visible(mainAdd) ? mainAdd : selectedSize;
        fallback?.focus({ preventScroll: true });
      }
    }
  };

  const updateStickySuppression = () => {
    if (stickyVisibilityFrame) return;
    stickyVisibilityFrame = window.requestAnimationFrame(() => {
      stickyVisibilityFrame = 0;
      applyStickySuppression();
    });
  };

  const scheduleStickySuppressionUpdate = updateStickySuppression;

  const suppressStickyNearPrimaryAction = () => {
    if (!isEditorialFocus) return;

    const mainAdd = mainProductForm()?.querySelector('[data-atc-form]');
    const sticky = document.querySelector('[data-sticky-addtocart]');
    if (!mainAdd || !sticky) return;

    if (observedPrimaryAction === mainAdd && stickyPrimaryObserver) {
      updateStickySuppression();
      return true;
    }

    stickyPrimaryObserver?.disconnect();
    observedPrimaryAction = mainAdd;
    primaryActionVisible = null;

    if (sticky.dataset.evanoVisibilityObserved !== 'true') {
      sticky.dataset.evanoVisibilityObserved = 'true';
      window.addEventListener('scroll', scheduleStickySuppressionUpdate, { passive: true });
      window.addEventListener('resize', scheduleStickySuppressionUpdate);
    }
    if (!('IntersectionObserver' in window)) {
      updateStickySuppression();
      return true;
    }

    stickyPrimaryObserver = new IntersectionObserver(
      (entries) => {
        let updated = false;
        for (const entry of entries) {
          if (entry.target !== observedPrimaryAction) continue;
          primaryActionVisible = entry.isIntersecting
            && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0;
          updated = true;
        }
        if (!updated) return;
        updateStickySuppression();
      },
      { threshold: 0.01 }
    );
    stickyPrimaryObserver.observe(mainAdd);

    const footer = document.querySelector('#t4s-footer');
    if (footer && footer !== observedFooter) {
      stickyFooterObserver?.disconnect();
      observedFooter = footer;
      stickyFooterObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.target !== observedFooter) return;
          footerNearViewport = entry.isIntersecting;
          updateStickySuppression();
        },
        { rootMargin: '0px 0px 48px 0px', threshold: 0 }
      );
      stickyFooterObserver.observe(footer);
    }

    updateStickySuppression();
    return true;
  };

  const placeSizeGuide = () => {
    const titles = [...document.querySelectorAll('.t4s-product__info-container .t4s-swatch__title')];
    const sizeTitle = titles.find((node) => /talla|size/.test(normalize(node.textContent)));
    if (!sizeTitle || sizeTitle.querySelector('.evano-size-guide-inline')) return false;

    const candidates = [...document.querySelectorAll('.t4s-product__info-container a, .t4s-product__info-container button')];
    const guide = candidates.find((node) => {
      if (node.closest('.t4s-swatch__list')) return false;
      return /guía de tallas|guia de tallas|size guide/.test(normalize(node.textContent));
    });
    if (!guide) return false;

    const source = guide.parentElement;
    const dialog = source?.querySelector('[data-evano-size-guide-dialog]');
    if (dialog) {
      if (dialog.parentElement !== document.body) document.body.append(dialog);
      if (!dialog.dataset.evanoSizeGuideBound) {
        dialog.dataset.evanoSizeGuideBound = 'true';
        dialog.addEventListener('close', () => {
          if (sizeGuideHistoryActive && !sizeGuideClosingFromHistory) {
            sizeGuideHistoryActive = false;
            window.history.back();
          }
          body.classList.remove('evano-size-guide-open');
          updateStickySuppression();
          sizeGuideReturnFocus?.focus();
          sizeGuideReturnFocus = null;
        });
      }
    }
    guide.classList.add('evano-size-guide-inline');
    sizeTitle.append(guide);
    if (source && source !== sizeTitle) source.classList.add('evano-size-guide-source');
    return true;
  };

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest?.('[data-evano-size-guide-open]');
    if (openButton) {
      const dialogId = openButton.getAttribute('aria-controls');
      const dialog = dialogId ? document.getElementById(dialogId) : null;
      if (!dialog) return;

      sizeGuideReturnFocus = openButton;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      try {
        window.history.pushState({ ...window.history.state, evanoSizeGuide: true }, '', window.location.href);
        sizeGuideHistoryActive = true;
      } catch (_) {
        sizeGuideHistoryActive = false;
      }
      body.classList.add('evano-size-guide-open');
      dialog.querySelector('[data-evano-size-guide-close]')?.focus();
      pushCroEvent('size_guide_opened');
      updateStickySuppression();
      return;
    }

    const closeButton = event.target.closest?.('[data-evano-size-guide-close]');
    const dialog = closeButton?.closest('[data-evano-size-guide-dialog]');
    if (dialog) {
      if (sizeGuideHistoryActive) window.history.back();
      else dialog.close();
    }
  });

  document.addEventListener('click', (event) => {
    const dialog = event.target.closest?.('[data-evano-size-guide-dialog]');
    if (dialog && event.target === dialog) {
      if (sizeGuideHistoryActive) window.history.back();
      else dialog.close();
    }
  });

  window.addEventListener('popstate', () => {
    if (!sizeGuideHistoryActive) return;
    sizeGuideHistoryActive = false;
    const dialog = document.querySelector('[data-evano-size-guide-dialog][open]');
    if (!dialog) return;
    sizeGuideClosingFromHistory = true;
    dialog.close();
    sizeGuideClosingFromHistory = false;
  });

  const reviewSection = document.querySelector('[id$="__reviews_editorial"]');
  const updateReviews = () => {
    if (!reviewSection) return;
    const text = normalize(reviewSection.textContent || '');
    const empty = /aún no hay reseñas|aun no hay reseñas|no reviews yet|sé el primero|se el primero|lidera el camino/.test(text);
    const reviewCards = reviewSection.querySelectorAll('[data-review-id], [class*="review-item"], [class*="ReviewCard"], [class*="review-card"]');
    reviewSection.classList.toggle('evano-reviews--ready', !empty && reviewCards.length > 0);
  };

  const preloadSwatch = (swatch) => {
    if (!swatch) return;
    const image = swatch.dataset.bg ||
      getComputedStyle(swatch).backgroundImage.match(/url\(["']?(.*?)["']?\)/)?.[1];
    if (!image) return;
    const imageUrl = image.startsWith('//') ? `${window.location.protocol}${image}` : image;
    new Image().src = imageUrl;
  };

  const variantImageUrl = (variant) =>
    variant?.featured_media?.preview_image?.src || variant?.featured_image?.src || '';

  const hydrateColorSwatches = () => {
    document.querySelectorAll('[data-swatch-option].is-t4s-name__color').forEach((group) => {
      const form = group.closest('form[data-type="add-to-cart-form"]');
      const optionIndex = Number.parseInt(group.dataset.id || '', 10);
      const variants = productVariantsForForm(form);
      if (!variants.length || !Number.isInteger(optionIndex)) return;

      group.querySelectorAll('[data-swatch-item]').forEach((swatch) => {
        const value = swatch.dataset.value || swatch.textContent.trim();
        const matches = variants.filter((variant) => variant.options?.[optionIndex] === value);
        const variant = matches.find((candidate) => candidate.available && variantImageUrl(candidate))
          || matches.find((candidate) => variantImageUrl(candidate));
        const image = variantImageUrl(variant);
        if (!image) return;

        const imageUrl = image.startsWith('//') ? `${window.location.protocol}${image}` : image;
        const sizeSeparator = imageUrl.includes('?') ? '&' : '?';
        swatch.dataset.bg = imageUrl;
        swatch.style.backgroundImage = `url("${imageUrl.replace(/"/g, '%22')}${sizeSeparator}width=160")`;
      });
    });
  };

  revealMetaOffer();
  placeSizeGuide();
  updateReviews();
  hydrateColorSwatches();
  window.setTimeout(hydrateColorSwatches, 600);
  document.addEventListener('pointerover', (event) => {
    preloadSwatch(event.target.closest('.t4s-swatch__item.is-sw__color'));
  }, { passive: true });

  if (isEditorialFocus) {
    document.addEventListener('evano:sticky-add-request', (request) => {
      if (handleStickyAdd(request.detail?.originalEvent)) request.preventDefault();
    });

    window.addEventListener(
      'click',
      (event) => {
        const mainAdd = event.target.closest?.(`${mainFormSelector} [data-atc-form]`);
        if (mainAdd) {
          void submitMainSelection(event, mainAdd);
          return;
        }

        const stickyAdd = event.target.closest?.('[data-sticky-addtocart] [data-action-atc]');

        if (stickyAdd) {
          handleStickyAdd(event);
          return;
        }

        const colorOption = event.target.closest?.(
          `${mainFormSelector} [data-swatch-option].is-t4s-name__color [data-swatch-item]`
        );
        if (colorOption && !colorOption.classList.contains('is--selected')) {
          // Let Gecko process the option first. Blocking the native click left its
          // internal product-options state incomplete even though our UI and the
          // hidden variant select looked correct, so native add-to-cart rejected
          // valid selections with "Seleccione algunas opciones de producto".
          window.setTimeout(() => {
            selectEditorialColor(colorOption);
            const picker = document.querySelector('.evano-sticky-size-picker');
            if (!picker || picker.hidden) return;
            const rebuilt = buildStickySizePicker();
            if (rebuilt) rebuilt.hidden = false;
          }, 0);
          return;
        }

        const sizeOption = event.target.closest?.(`${sizeGroupSelector} [data-swatch-item]`);
        if (sizeOption) {
          const alreadySelected = sizeOption.classList.contains('is--selected')
            && confirmedSize(sizeOption.closest(sizeGroupSelector)) === (sizeOption.dataset.value || sizeOption.textContent.trim());
          if (!alreadySelected) trackSizeSelection(sizeOption);
          sizeOption.closest('form')
            ?.querySelectorAll('.evano-variant-choice-error')
            .forEach((node) => node.remove());
        }

        if (sizeOption || event.target.closest?.('[data-swatch-item], [data-dropdown-item]')) {
          schedulePurchaseSync();
          window.setTimeout(schedulePurchaseSync, 180);
          window.setTimeout(schedulePurchaseSync, 700);
        }

        window.setTimeout(updateStickySuppression, 0);
        window.setTimeout(updateStickySuppression, 260);
      },
      true
    );

    window.addEventListener(
      'submit',
      (event) => {
        const form = event.target.closest?.(mainFormSelector);
        if (!form) return;
        const submit = form.querySelector('[data-atc-form]');
        if (submit) void submitMainSelection(event, submit);
      },
      true
    );

    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeStickySizePicker();

      const option = event.target.closest?.('.evano-sticky-size-picker__option[role="radio"]');
      if (!option || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const options = [...option.closest('[role="radiogroup"]')
        .querySelectorAll('[role="radio"]:not([disabled])')];
      const currentIndex = options.indexOf(option);
      if (currentIndex < 0 || options.length < 2) return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
      const next = options[(currentIndex + direction + options.length) % options.length];
      next.focus();
      next.click();
    });

    document.addEventListener('evano:cart-added', updateStickySuppression);
    document.addEventListener('cart:refresh', updateStickySuppression);
    document.addEventListener('evano:cart-drawer-closed', updateStickySuppression);
    document.addEventListener('evano:explicit-variant-resolved', (event) => {
      schedulePurchaseSync();
      window.setTimeout(schedulePurchaseSync, 120);
      window.setTimeout(schedulePurchaseSync, 420);
      const variantId = String(event.detail?.variantId || '');
      if (!variantId || variantId === lastTrackedVariantId) return;
      lastTrackedVariantId = variantId;
      pushCroEvent('variant_selected', {
        variant_id: variantId,
        size: confirmedSize() || '',
        color: activeColor()
      });
    });
    document.addEventListener('change', (event) => {
      if (event.target.matches?.('form[data-type="add-to-cart-form"] select[name="id"]')) {
        schedulePurchaseSync();
      }
    });

    schedulePurchaseSync();
    syncDeepLinkedVariantPrice();
    window.setTimeout(syncDeepLinkedVariantPrice, 180);
    suppressStickyNearPrimaryAction();
  }

  let pendingMutationFrame = 0;
  let pendingReviewMutation = false;
  const observer = new MutationObserver((records) => {
    if (reviewSection && records.some((record) => reviewSection.contains(record.target))) {
      pendingReviewMutation = true;
    }
    if (pendingMutationFrame) return;
    pendingMutationFrame = window.requestAnimationFrame(() => {
      pendingMutationFrame = 0;
      const refreshReviews = pendingReviewMutation;
      pendingReviewMutation = false;
      placeSizeGuide();
      if (refreshReviews) updateReviews();
      schedulePurchaseSync();
      if (!suppressStickyNearPrimaryAction()) updateStickySuppression();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
  window.setTimeout(() => observer.disconnect(), 12000);
})();
